import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { PushJobMode, PushJobSummary } from "@tg/shared";
import {
  pushApi,
  type PushJobItemView,
  type PushSettingsPublic,
  type ScriptPushLimits
} from "../../lib/push-api.js";
import { PushItemsReport } from "./push-items-report.js";
import { ScriptPushForm, type ScriptPushValue } from "./script-push-form.js";
import { useRealtimeStore } from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import { formatFullDateTime } from "../../lib/datetime.js";
import {
  PUSH_ERROR_LABELS,
  PUSH_SKIP_LABELS,
  PUSH_STATUS_LABELS,
  formatReasonCounts,
  isUnfinishedPushStatus,
  pushModeLabel,
  pushQueueStateLabel
} from "./push-labels.js";

// Модалка «Пуш по папке» (раунд 46): показывает настройки аккаунта, ставит
// папку на пуш и рисует живой прогресс (store.pushJobs ← push.jobUpdated, плюс
// страховочный поллинг раз в 15 с). Закрытие модалки job НЕ останавливает.
// С 2026-10-09 по аккаунту идёт несколько папок сразу: лишние ждут «в
// очереди» и стартуют сами; модалка показывает всю очередь аккаунта.
// С 2026-10-13 (запрос №10) та же модалка запускает пуш своими скриптами
// (mode="scripts"): вместо настроек ИИ — форма текстов и паузы; очередь,
// прогресс, отмена и уведомление — общие с ИИ-пушем.

interface PushJobModalProps {
  accountId: string;
  listId: string;
  listTitle: string;
  mode: PushJobMode;
  onClose(): void;
}

export const PushJobProgress = ({ job }: { job: PushJobSummary }) => {
  const timeZone = useUiPrefs((state) => state.timeZone);
  const skips = formatReasonCounts(job.skipReasons, PUSH_SKIP_LABELS);
  const errors = formatReasonCounts(job.errorReasons, PUSH_ERROR_LABELS);
  return (
    <div className="push-progress">
      <div className="push-progress__status">
        <strong>{PUSH_STATUS_LABELS[job.status]}</strong>
        {job.mode === "scripts" ? (
          <span>
            {" "}
            · скрипты: {job.scriptsCount ?? 0} текст., пауза {job.delayMinSec}–{job.delayMaxSec} с
          </span>
        ) : null}
        {job.status === "paused" && job.pausedUntil ? (
          <span> до {formatFullDateTime(job.pausedUntil, timeZone)}</span>
        ) : null}
        {job.errorMessage ? <span className="push-progress__error"> · {job.errorMessage}</span> : null}
      </div>
      <dl className="push-progress__counters">
        <div>
          <dt>Просмотрено</dt>
          <dd>{job.scannedCount}</dd>
        </div>
        <div>
          <dt>Подходящих</dt>
          <dd>{job.foundCount}</dd>
        </div>
        <div>
          <dt>Отправлено</dt>
          <dd>{job.sentCount}</dd>
        </div>
        <div>
          <dt>Пропущено</dt>
          <dd>{job.skippedCount}</dd>
        </div>
        <div>
          <dt>Ошибок</dt>
          <dd>{job.errorCount}</dd>
        </div>
      </dl>
      {skips.length > 0 ? (
        <div className="push-progress__reasons">
          <span>Причины пропуска:</span>
          <ul>
            {skips.map((entry) => (
              <li key={entry.label}>
                {entry.label} — {entry.count}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {errors.length > 0 ? (
        <div className="push-progress__reasons">
          <span>Ошибки:</span>
          <ul>
            {errors.map((entry) => (
              <li key={entry.label}>
                {entry.label} — {entry.count}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="push-progress__meta">
        Запустил {job.startedByLogin} · {formatFullDateTime(job.createdAt, timeZone)}
        {job.finishedAt ? ` → ${formatFullDateTime(job.finishedAt, timeZone)}` : ""}
      </div>
    </div>
  );
};

export const PushJobModal = ({ accountId, listId, listTitle, mode, onClose }: PushJobModalProps) => {
  const wsUrl = useRealtimeStore((state) => state.wsUrl);
  const timeZone = useUiPrefs((state) => state.timeZone);
  const pushJobs = useRealtimeStore((state) => state.pushJobs);
  const upsertPushJob = useRealtimeStore((state) => state.upsertPushJob);
  const appRole = useRealtimeStore((state) => state.appRole);
  const appUserId = useRealtimeStore((state) => state.appUserId);
  const [settings, setSettings] = useState<{
    canStart: boolean;
    configured: boolean;
    integrationConfigured: boolean;
    settings: PushSettingsPublic;
    scriptLimits: ScriptPushLimits;
  } | null>(null);
  const isScripts = mode === "scripts";
  const [scriptValue, setScriptValue] = useState<ScriptPushValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  // «Удалить папку после завершения» для новой постановки. Удалять умеем
  // только наши локальные папки — папки Telegram общие с телефоном.
  const isLocalFolder = listId.startsWith("local:");
  const [deleteAfter, setDeleteAfter] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    pushApi
      .getSettings(accountId, wsUrl)
      .then((result) => {
        if (cancelled) return;
        setSettings(result);
        result.activeJobs.forEach(upsertPushJob);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Не удалось загрузить настройки");
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, upsertPushJob, wsUrl]);

  // Запуски этой папки и раскрытый разбор по чатам. Детали («Что ушло в ИИ»,
  // ответ, имя) сервер отдаёт менеджерам и тому, кто запускал — саппорт видит
  // свои пропуши целиком, чужие — только статусы.
  const [history, setHistory] = useState<PushJobSummary[]>([]);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<PushJobItemView[] | null>(null);
  const [openScripts, setOpenScripts] = useState<string[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // Без подписки на state.chats — иначе модалка перерисовывалась бы на каждом
  // входящем сообщении; имя нужно только в момент рендера строки.
  const chatTitle = (chatId: string): string | undefined =>
    useRealtimeStore.getState().chats[chatId]?.title;

  const accountJobs = useMemo(
    () => Object.values(pushJobs).filter((job) => job.accountId === accountId),
    [accountId, pushJobs]
  );
  // Очередь аккаунта: незавершённые по порядку постановки + завершённые за
  // эту сессию (чтобы «Завершено» было видно, пока модалка открыта).
  const queue = useMemo(() => {
    const unfinished = accountJobs
      .filter((job) => isUnfinishedPushStatus(job.status))
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
    const finished = accountJobs
      .filter((job) => !isUnfinishedPushStatus(job.status))
      .sort((left, right) => (right.finishedAt ?? right.updatedAt).localeCompare(left.finishedAt ?? left.updatedAt))
      .slice(0, 5);
    return [...unfinished, ...finished];
  }, [accountJobs]);
  const hasUnfinished = queue.some((job) => isUnfinishedPushStatus(job.status));
  // Текущий пуш этой папки: незавершённый, иначе последний из стора.
  const job = useMemo(() => {
    const ofFolder = accountJobs
      .filter((entry) => entry.listId === listId)
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    return ofFolder.find((entry) => isUnfinishedPushStatus(entry.status)) ?? ofFolder[0] ?? null;
  }, [accountJobs, listId]);
  const folderBusy = job ? isUnfinishedPushStatus(job.status) : false;

  const canManage = (entry: PushJobSummary): boolean =>
    appRole === "admin" ||
    appRole === "geo_admin" ||
    (appUserId !== null && entry.startedByUserId === appUserId);

  const loadHistory = useCallback(async (): Promise<void> => {
    try {
      const { jobs } = await pushApi.listJobs(accountId, wsUrl, 30);
      setHistory(jobs.filter((entry) => entry.listId === listId));
    } catch (cause) {
      setHistoryError(cause instanceof Error ? cause.message : "Не удалось загрузить запуски");
    }
  }, [accountId, listId, wsUrl]);

  const loadItems = useCallback(
    async (jobId: string): Promise<void> => {
      try {
        const { job: fresh, items, scripts } = await pushApi.getJob(jobId, wsUrl);
        setOpenItems(items);
        setOpenScripts(scripts ?? null);
        setHistory((current) => current.map((entry) => (entry.jobId === jobId ? fresh : entry)));
        upsertPushJob(fresh);
      } catch (cause) {
        setHistoryError(cause instanceof Error ? cause.message : "Не удалось загрузить разбор");
      }
    },
    [upsertPushJob, wsUrl]
  );

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Пуш этой папки завершился — обновить историю, чтобы он попал в неё с итогами.
  const jobStatus = job?.status;
  useEffect(() => {
    if (jobStatus && !isUnfinishedPushStatus(jobStatus)) void loadHistory();
  }, [jobStatus, loadHistory]);

  // Страховочный поллинг: если WS-событие потерялось (реконнект), раз в 15 с
  // перечитываем последние пуши аккаунта, пока в очереди что-то не завершено;
  // раскрытый разбор — тоже.
  useEffect(() => {
    if (!hasUnfinished) return;
    const timer = window.setInterval(() => {
      if (openJobId) {
        void loadItems(openJobId);
      }
      pushApi
        .listJobs(accountId, wsUrl, 50)
        .then(({ jobs }) => {
          const known = useRealtimeStore.getState().pushJobs;
          for (const entry of jobs) {
            if (known[entry.jobId] || isUnfinishedPushStatus(entry.status)) upsertPushJob(entry);
          }
        })
        .catch(() => undefined);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [accountId, hasUnfinished, loadItems, openJobId, upsertPushJob, wsUrl]);

  const toggleRun = (jobId: string): void => {
    if (openJobId === jobId) {
      setOpenJobId(null);
      setOpenItems(null);
      setOpenScripts(null);
      return;
    }
    setOpenJobId(jobId);
    setOpenItems(null);
    setOpenScripts(null);
    void loadItems(jobId);
  };

  // Текущий job (из store, живой) — первым; остальные из истории.
  const runs: PushJobSummary[] = job
    ? [job, ...history.filter((entry) => entry.jobId !== job.jobId)]
    : history;

  const start = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const base = { accountId, listId, listTitle, deleteFolderOnFinish: isLocalFolder && deleteAfter };
      const { job: created } = await pushApi.startJob(
        isScripts && scriptValue
          ? {
              ...base,
              mode: "scripts",
              scripts: scriptValue.texts,
              delayMinSec: scriptValue.delayMinSec,
              delayMaxSec: scriptValue.delayMaxSec,
              maxChatsPerRun: scriptValue.maxChatsPerRun
            }
          : base,
        wsUrl
      );
      upsertPushJob(created);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось запустить");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (entry: PushJobSummary): Promise<void> => {
    const question =
      entry.status === "queued"
        ? `Убрать папку «${entry.listTitle}» из очереди?`
        : `Остановить пуш папки «${entry.listTitle}»? Уже отправленные сообщения останутся.`;
    if (!window.confirm(question)) return;
    setBusyJobId(entry.jobId);
    setError(null);
    try {
      const { job: updated } = await pushApi.cancelJob(entry.jobId, wsUrl);
      upsertPushJob(updated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось отменить");
    } finally {
      setBusyJobId(null);
    }
  };

  const toggleDeleteAfter = async (entry: PushJobSummary, value: boolean): Promise<void> => {
    setBusyJobId(entry.jobId);
    setError(null);
    try {
      const { job: updated } = await pushApi.updateJob(
        entry.jobId,
        { deleteFolderOnFinish: value },
        wsUrl
      );
      upsertPushJob(updated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось изменить настройку");
    } finally {
      setBusyJobId(null);
    }
  };

  const cfg = settings?.settings;

  return createPortal(
    <div
      className="folder-editor"
      role="dialog"
      aria-modal="true"
      aria-label={isScripts ? "Пуш по папке (свои скрипты)" : "Пуш по папке"}
    >
      <button type="button" className="folder-editor__backdrop" onClick={onClose} aria-label="Закрыть" />
      <div className="folder-editor__panel push-modal">
        <div className="folder-editor__header">
          <strong>
            {isScripts ? "Пуш своими скриптами" : "Пуш по папке (ИИ)"} — «{listTitle}»
          </strong>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        {error ? <div className="side-card__error">{error}</div> : null}
        {!settings ? (
          <div className="folder-autopick__note">Загружаем настройки…</div>
        ) : (
          <>
            {isScripts ? (
              <>
                <div className="folder-autopick__note">
                  Система отберёт из папки личные чаты, где последнее сообщение — наше и
                  прочитано без ответа, и отправит каждому один из ваших текстов (по кругу).
                  Несколько папок можно ставить сразу: если нагрузка позволяет, они идут
                  параллельно, иначе ждут в очереди и запускаются сами.
                </div>
                {!folderBusy ? (
                  <ScriptPushForm
                    accountId={accountId}
                    wsUrl={wsUrl}
                    limits={settings.scriptLimits}
                    defaultMaxChats={settings.settings.maxChatsPerRun}
                    onChange={setScriptValue}
                  />
                ) : null}
              </>
            ) : (
              <>
                <div className="folder-autopick__note">
                  Система отберёт из папки личные чаты, где последнее сообщение — наше и
                  прочитано без ответа, и отправит каждому индивидуальный follow-up от ИИ.
                  Несколько папок можно ставить сразу: если нагрузка позволяет, они идут
                  параллельно, иначе ждут в очереди и запускаются сами.
                </div>
                <dl className="push-modal__settings">
                  <div>
                    <dt>Модель</dt>
                    <dd>{cfg?.model || "не задана"}</dd>
                  </div>
                  <div>
                    <dt>Промпт</dt>
                    <dd>{cfg && cfg.promptLength > 0 ? `${cfg.promptLength} симв.` : "не задан"}</dd>
                  </div>
                  <div>
                    <dt>Контекст</dt>
                    <dd>{cfg?.contextMessages} сообщ.</dd>
                  </div>
                  <div>
                    <dt>Пауза между отправками</dt>
                    <dd>
                      {cfg?.delayMinSec}–{cfg?.delayMaxSec} с
                    </dd>
                  </div>
                  <div>
                    <dt>Максимум чатов за запуск</dt>
                    <dd>{cfg?.maxChatsPerRun}</dd>
                  </div>
                </dl>
                {!settings.configured ? (
                  <div className="side-card__error">
                    Для этого аккаунта не заданы промпт и/или модель ИИ — администратор задаёт их в
                    Настройки → «ИИ-пропуш».
                  </div>
                ) : !settings.integrationConfigured ? (
                  <div className="side-card__error">
                    LLM-подключение (интеграция переводчика) не настроено или выключено.
                  </div>
                ) : null}
              </>
            )}
            {!folderBusy ? (
              <label
                className={`push-queue__delete-toggle${isLocalFolder ? "" : " push-queue__delete-toggle--disabled"}`}
                title={
                  isLocalFolder
                    ? undefined
                    : "Папки Telegram общие с телефоном и другими операторами — автоматически не удаляются"
                }
              >
                <input
                  type="checkbox"
                  checked={isLocalFolder && deleteAfter}
                  disabled={!isLocalFolder}
                  onChange={(event) => setDeleteAfter(event.target.checked)}
                />
                Удалить папку после завершения
                {!isLocalFolder ? <small> — только для своих папок, не папок Telegram</small> : null}
              </label>
            ) : null}
            {job && job.listId === listId && isUnfinishedPushStatus(job.status) && job.status !== "queued" ? (
              <PushJobProgress job={job} />
            ) : null}
            <div className="push-queue">
              <div className="push-items__title">
                Пуши аккаунта
                <small>Все папки, поставленные на пуш по этому аккаунту, — ваши и других операторов</small>
              </div>
              {queue.length === 0 ? (
                <div className="settings-card__hint">Сейчас по аккаунту пушей нет.</div>
              ) : (
                <ul className="push-queue__list">
                  {queue.map((entry) => {
                    const unfinished = isUnfinishedPushStatus(entry.status);
                    const manageable = canManage(entry);
                    const entryLocal = entry.listId.startsWith("local:");
                    const rowBusy = busyJobId === entry.jobId;
                    return (
                      <li
                        key={entry.jobId}
                        className={`push-queue__item${entry.listId === listId ? " push-queue__item--current" : ""}`}
                      >
                        <div className="push-queue__main">
                          <span className={`push-queue__state push-queue__state--${entry.status}`}>
                            {pushQueueStateLabel(entry.status)}
                          </span>
                          <strong className="push-queue__title">{entry.listTitle}</strong>
                          <span className="push-queue__mode">{pushModeLabel(entry.mode)}</span>
                          <span className="push-queue__meta">
                            {entry.status === "queued"
                              ? `поставил ${entry.startedByLogin}`
                              : `отправлено ${entry.sentCount} из ${entry.foundCount} · ${entry.startedByLogin}`}
                            {entry.status === "paused" ? " · пауза Telegram" : ""}
                          </span>
                        </div>
                        <div className="push-queue__actions">
                          {entryLocal ? (
                            <label className="push-queue__delete-toggle" title="Удалить папку после полного завершения пуша">
                              <input
                                type="checkbox"
                                checked={entry.deleteFolderOnFinish === true}
                                disabled={!unfinished || !manageable || rowBusy}
                                onChange={(event) => void toggleDeleteAfter(entry, event.target.checked)}
                              />
                              удалить после
                            </label>
                          ) : null}
                          {unfinished && manageable ? (
                            <button
                              type="button"
                              className="button button--ghost chat-selection-bar__danger push-queue__cancel"
                              onClick={() => void cancel(entry)}
                              disabled={rowBusy}
                            >
                              {entry.status === "queued" ? "Убрать из очереди" : "Остановить"}
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {historyError ? <div className="side-card__error">{historyError}</div> : null}
            <div className="push-runs">
              <div className="push-items__title">
                Запуски этой папки
                <small>Раскрой запуск — увидишь каждый чат и отправленный текст (для своих запусков)</small>
              </div>
              {runs.length === 0 ? (
                <div className="settings-card__hint">Запусков по этой папке ещё не было.</div>
              ) : (
                <ul className="push-runs__list">
                  {runs.map((run) => {
                    const open = openJobId === run.jobId;
                    return (
                      <li key={run.jobId} className="push-runs__item">
                        <button
                          type="button"
                          className="push-settings__job-head"
                          onClick={() => toggleRun(run.jobId)}
                          aria-expanded={open}
                        >
                          <span className="push-settings__job-title">
                            <strong>{PUSH_STATUS_LABELS[run.status]}</strong> · {pushModeLabel(run.mode)}
                            {job && run.jobId === job.jobId && isUnfinishedPushStatus(job.status) ? " · текущий" : ""}
                          </span>
                          <span className="push-settings__job-meta">
                            найдено {run.foundCount}, отправлено {run.sentCount}, пропущено {run.skippedCount},
                            ошибок {run.errorCount} · {formatFullDateTime(run.createdAt, timeZone)} ·{" "}
                            {run.startedByLogin}
                          </span>
                        </button>
                        {open ? (
                          <div className="push-settings__job-body">
                            {openItems === null ? (
                              <div className="folder-autopick__note">Загружаем разбор…</div>
                            ) : (
                              <>
                                {openScripts && openScripts.length > 0 ? (
                                  <details className="script-push__used">
                                    <summary>Тексты ротации ({openScripts.length})</summary>
                                    <ol>
                                      {openScripts.map((text, index) => (
                                        <li key={index}>{text}</li>
                                      ))}
                                    </ol>
                                  </details>
                                ) : null}
                                <PushItemsReport items={openItems} chatTitle={chatTitle} mode={run.mode ?? "ai"} />
                              </>
                            )}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
        <div className="folder-editor__actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            Закрыть
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void start()}
            disabled={
              busy ||
              folderBusy ||
              !settings ||
              !settings.canStart ||
              (isScripts
                ? !scriptValue || scriptValue.error !== null
                : !settings.configured || !settings.integrationConfigured)
            }
            title={folderBusy ? "Эта папка уже в очереди или в работе" : undefined}
          >
            {busy ? "Ставим…" : folderBusy ? "Папка уже на пуше" : job ? "Запустить ещё раз" : "Запустить"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
